import { select } from "redux-saga/effects";
import { IAction } from "reduxsauce";
import { AssessmentResult, IUser, IUsersAnalytics } from "../App/Analytics/Common";
import { IAssessmentTotals } from "../components/pages/Analytics/AssessmentAnalytics/AssessmentsGraph";
import { analyticsCourseAssessmentTotals } from "../parsers/analytics";
import * as selectors from "../selectors/selectors";
import { getPercentage } from "../utils";
import { exportDataAsCSV, exportDataAsXLSX, Table } from "./export-data";

type ColumnHeader =
  | "Organisation Title"
  | "Master Course Title"
  | "SCORM Export Title"
  | "Count of Choices / Assessment Responses"
  | "% Pre Assessment First"
  | "# Pre Assessment First"
  | "% Lesson First"
  | "# Lesson First"
  | "% Pre Assessment Correct"
  | "# Pre Assessment Correct"
  | "% Pre Assessment Incorrect"
  | "# Pre Assessment Incorrect"
  | "% First Post Assessment Correct"
  | "# First Post Assessment Correct"
  | "% First Post Assessment Incorrect"
  | "# First Post Assessment Incorrect";

type ColumnHeaderAll = ColumnHeader | "Package State";

type Row = [
  string,
  string,
  string,
  number,
  string,
  number,
  string,
  number,
  string,
  number,
  string,
  number,
  string,
  number,
  string,
  number,
  string?
];

export interface IAssessmentReportDataAction extends IAction {
  courseDisplayName: string;
  objectDisplayName: string;
  organizationName: string;
  assessmentsTotal: IAssessmentTotals;
  packageState?: string;
  isXLSX?: boolean;
}

const resultHeader: ColumnHeader[] = [
  "Organisation Title",
  "Master Course Title",
  "SCORM Export Title",
  "Count of Choices / Assessment Responses",
  "% Pre Assessment First",
  "# Pre Assessment First",
  "% Lesson First",
  "# Lesson First",
  "% Pre Assessment Correct",
  "# Pre Assessment Correct",
  "% Pre Assessment Incorrect",
  "# Pre Assessment Incorrect",
  "% First Post Assessment Correct",
  "# First Post Assessment Correct",
  "% First Post Assessment Incorrect",
  "# First Post Assessment Incorrect",
];

const resultHeaderAll: ColumnHeaderAll[] = [...resultHeader, "Package State"];

export function generateAssessmentReportData(action: IAssessmentReportDataAction): Table<ColumnHeader, Row> {
  const resultData = generateRow(action);
  return [resultHeader, resultData];
}

export const usersAnalyticsToTotal = (users: IUser[], usersAnalytics: IUsersAnalytics): IAssessmentTotals => {
  const assessments: AssessmentResult[][] = [];
  for (const user of users) {
    const userAnalytics = usersAnalytics[user.accountId];
    if (userAnalytics) {
      if (userAnalytics.completed) {
        const userAssessments = userAnalytics.assessments;
        const assessmentsLength = userAssessments.length;
        for (let i = 0; i < assessmentsLength; i++) {
          const assessment = userAssessments[i];
          assessments.push(assessment);
        }
      }
    }
  }
  return analyticsCourseAssessmentTotals(assessments);
};

export const generateRowAllAssessments = (action: IAssessmentReportDataAction): Row => {
  const {
    totalAssessments,
    totalPreAssigmentsNotTaken,
    totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
    totalPreAssigmentsTaken,
    totalPreAssigmentsTakenPassedOnFirst,
  }: IAssessmentTotals = action.assessmentsTotal;

  const totalPreAssigmentsIncorrect = totalPreAssigmentsTaken - totalPreAssigmentsTakenPassedOnFirst;
  const totalPostAssignmentsIncorrect =
    totalPreAssigmentsNotTaken - totalPreAssigmentsNotTakenPassedOnFirstPostAssessment;

  return [
    action.organizationName,
    action.courseDisplayName,
    action.objectDisplayName,
    totalAssessments,
    `${getPercentage(totalPreAssigmentsTaken, totalAssessments)}%`,
    totalPreAssigmentsTaken,
    `${getPercentage(totalPreAssigmentsNotTaken, totalAssessments)}%`,
    totalPreAssigmentsNotTaken,
    `${getPercentage(totalPreAssigmentsTakenPassedOnFirst, totalPreAssigmentsTaken)}%`,
    totalPreAssigmentsTakenPassedOnFirst,
    `${getPercentage(totalPreAssigmentsIncorrect, totalPreAssigmentsTaken)}%`,
    totalPreAssigmentsIncorrect,
    `${getPercentage(totalPreAssigmentsNotTakenPassedOnFirstPostAssessment, totalPreAssigmentsNotTaken)}%`,
    totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
    `${getPercentage(totalPostAssignmentsIncorrect, totalPreAssigmentsNotTaken)}%`,
    totalPostAssignmentsIncorrect,
    action.packageState,
  ];
};

export const generateRow = (action: IAssessmentReportDataAction): Row => {
  const {
    totalAssessments,
    totalPreAssigmentsNotTaken,
    totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
    totalPreAssigmentsTaken,
    totalPreAssigmentsTakenPassedOnFirst,
  }: IAssessmentTotals = action.assessmentsTotal;

  if (totalAssessments === 0) {
    return [
      action.organizationName,
      action.courseDisplayName,
      action.objectDisplayName,
      0,
      "0%",
      0,
      "0%",
      0,
      "0%",
      0,
      "0%",
      0,
      "0%",
      0,
      "0%",
      0,
    ];
  }

  const totalPreAssigmentsIncorrect = totalPreAssigmentsTaken - totalPreAssigmentsTakenPassedOnFirst;
  const totalPostAssignmentsIncorrect =
    totalPreAssigmentsNotTaken - totalPreAssigmentsNotTakenPassedOnFirstPostAssessment;

  return [
    action.organizationName,
    action.courseDisplayName,
    action.objectDisplayName,
    totalAssessments,
    `${getPercentage(totalPreAssigmentsTaken, totalAssessments)}%`,
    totalPreAssigmentsTaken,
    `${getPercentage(totalPreAssigmentsNotTaken, totalAssessments)}%`,
    totalPreAssigmentsNotTaken,
    `${getPercentage(totalPreAssigmentsTakenPassedOnFirst, totalPreAssigmentsTaken)}%`,
    totalPreAssigmentsTakenPassedOnFirst,
    `${getPercentage(totalPreAssigmentsIncorrect, totalPreAssigmentsTaken)}%`,
    totalPreAssigmentsIncorrect,
    `${getPercentage(totalPreAssigmentsNotTakenPassedOnFirstPostAssessment, totalPreAssigmentsNotTaken)}%`,
    totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
    `${getPercentage(totalPostAssignmentsIncorrect, totalPreAssigmentsNotTaken)}%`,
    totalPostAssignmentsIncorrect,
  ];
};

export function* exportAllAssessments(rows: Table<ColumnHeaderAll, Row>) {
  const tableHeader: Table<ColumnHeaderAll, Row> = [resultHeaderAll];
  yield exportDataAsCSV(tableHeader.concat(rows));
}

export function* exportAssessments(action: IAssessmentReportDataAction) {
  const organizationName = yield select(selectors.organizationName);
  const resultData = generateAssessmentReportData({
    ...action,
    organizationName,
  });
  action.isXLSX ? yield exportDataAsXLSX(resultData, "chart") : yield exportDataAsCSV(resultData);
}
